import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Package, User, CreditCard, FileText, Truck, Camera, 
  CheckCircle, AlertTriangle, Scan, Upload, ExternalLink, Loader2, 
  Check, MapPin, Phone, Mail, ChevronRight, X, Trash2, Plus,
  Download, Shield, Receipt, Hash, Calendar, Euro, Building
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import { compressImage } from '../lib/imageCompress';

const API = `${window.location.origin}/api`;

// Pack images from the website - mapping by pack_id AND pack name variations
const PACK_IMAGES = {
  // By pack_id (with variations)
  'pack_dj_booth': 'https://customer-assets.emergentagent.com/job_101ab4cd-4d37-431a-b507-ce3ea7ef67e4/artifacts/gu1bc6jg_Pack%20%C3%A9cran%20LED%202m2.png',
  'pack_djbooth': 'https://customer-assets.emergentagent.com/job_101ab4cd-4d37-431a-b507-ce3ea7ef67e4/artifacts/gu1bc6jg_Pack%20%C3%A9cran%20LED%202m2.png',
  'pack_compact': 'https://customer-assets.emergentagent.com/job_101ab4cd-4d37-431a-b507-ce3ea7ef67e4/artifacts/vm87efqp_Pack%20%C3%A9cran%20LED%204m2.png',
  'pack_standard': 'https://customer-assets.emergentagent.com/job_101ab4cd-4d37-431a-b507-ce3ea7ef67e4/artifacts/nenk0vgh_Pack%20%C3%A9cran%20LED%207m2.png',
  'pack_xl': 'https://customer-assets.emergentagent.com/job_101ab4cd-4d37-431a-b507-ce3ea7ef67e4/artifacts/uncbzflz_Pack%20%C3%A9cran%20LED%2015m2.png',
  'default': 'https://customer-assets.emergentagent.com/job_101ab4cd-4d37-431a-b507-ce3ea7ef67e4/artifacts/nenk0vgh_Pack%20%C3%A9cran%20LED%207m2.png'
};

// Function to get pack image based on pack_id or pack_name
const getPackImageUrl = (packId, packName) => {
  // Direct match by pack_id (always coerce to string — pack_id can be numeric)
  const idStr = packId != null ? String(packId) : '';
  if (idStr && PACK_IMAGES[idStr]) {
    return PACK_IMAGES[idStr];
  }

  // Match by pack name keywords
  const nameLower = String(packName || '').toLowerCase();
  const idLower = idStr.toLowerCase();
  
  if (nameLower.includes('dj') || nameLower.includes('2m') || nameLower.includes('cabine') || idLower.includes('djbooth')) {
    return PACK_IMAGES['pack_dj_booth'];
  }
  if (nameLower.includes('compact') || nameLower.includes('4m')) {
    return PACK_IMAGES['pack_compact'];
  }
  if (nameLower.includes('standard') || nameLower.includes('7m')) {
    return PACK_IMAGES['pack_standard'];
  }
  if (nameLower.includes('xl') || nameLower.includes('15m')) {
    return PACK_IMAGES['pack_xl'];
  }
  
  return PACK_IMAGES.default;
};

// Liste de repli des vidéo processeurs (catalogue Phocea Light) — utilisée si
// le catalogue SKU n'est pas joignable. La liste affichée est normalement
// chargée dynamiquement depuis les produits de catégorie VIDEO_PROCESSEUR.
const VIDEO_PROCESSORS = [
  'Novastar TB30',
  'Novastar TU15 Pro',
  'Novastar TB50',
  'Novastar TB60',
  'NovaStar KU20',
  'Novastar TU20 Pro',
  'VX400 Pro Novastar',
  'Novastar CVT10 S',
  'VX600 Pro Novastar',
  'MX20 Novastar',
  'VX1000 PRO Novastar',
  'Novastar TU40 Pro',
  'MX30 Novastar',
  'Novastar TU4K Pro',
  'VX16S Novastar',
  'Novastar MCTRL 4k',
  'NovaPro UHD Jr',
  'MX40 PRO Novastar',
];

// Nettoie le nom catalogue ("Vidéo processeur NOVASTAR TB30") en libellé court
// ("NOVASTAR TB30") pour l'afficher dans le sélecteur de préparation.
const cleanVideoProcessorLabel = (name) =>
  (name || '').replace(/^vid[ée]o\s+processeur\s+/i, '').trim();

// Options de repli (objets {sku, name, label}) si le catalogue est injoignable.
const FALLBACK_VP_OPTIONS = VIDEO_PROCESSORS.map((vp) => ({ sku: '', name: vp, label: vp }));

// Adresse de livraison : stockée aux champs top-level delivery_* ; repli sur les
// champs customer.* (anciennes commandes / commandes manuelles).
const getDeliveryAddress = (reservation) => {
  const c = reservation?.customer || {};
  const street = reservation?.delivery_address || c.address || '';
  const postal = reservation?.delivery_postal_code || c.postalCode || c.postal_code || '';
  const city = reservation?.delivery_city || c.city || '';
  const line2 = `${postal} ${city}`.trim();
  const full = [street, line2].filter(Boolean).join(', ');
  return { street, postal, city, line2, full };
};

const OrderProcessingPage = () => {
  const { reservationId } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [reservation, setReservation] = useState(null);
  const [packComponents, setPackComponents] = useState([]);
  const [videoProcessor, setVideoProcessor] = useState('');
  const [savingProcessor, setSavingProcessor] = useState(false);
  // Liste des modèles vidéo processeur sélectionnables, alignée sur le catalogue
  // SKU (catégorie VIDEO_PROCESSEUR). Repli sur la liste statique au besoin.
  const [videoProcessorOptions, setVideoProcessorOptions] = useState(FALLBACK_VP_OPTIONS);
  
  // Current step: 1 = Scan, 2 = Inspection, 3 = Shipping
  const [currentStep, setCurrentStep] = useState(1);
  
  // Scanning state
  const [scanInput, setScanInput] = useState('');
  const [scanning, setScanning] = useState(false);
  const [scannedItems, setScannedItems] = useState({});
  const scanInputRef = useRef(null);
  
  // Inspection state
  const [inspectionPhotos, setInspectionPhotos] = useState([]);
  const [inspectionNotes, setInspectionNotes] = useState('');
  
  // Shipping state
  const [shippingInfo, setShippingInfo] = useState({
    carrier: 'Dachser',
    trackingNumber: '',
    trackingUrl: '',
    estimatedDelivery: ''
  });

  useEffect(() => {
    fetchOrderDetails();
  }, [reservationId]);

  // Charge la liste des vidéo processeurs depuis le catalogue SKU pour que le
  // sélecteur de préparation reste synchronisé avec les produits enregistrés.
  useEffect(() => {
    const fetchVideoProcessorCatalog = async () => {
      try {
        const res = await axios.get(`${API}/admin/products`, { withCredentials: true });
        const opts = (res.data || [])
          .filter((p) => p.category === 'VIDEO_PROCESSEUR')
          .map((p) => ({ sku: p.sku, name: p.name, label: cleanVideoProcessorLabel(p.name) }))
          .filter((o) => o.label);
        // Dédoublonne par libellé + tri alphabétique
        const seen = new Set();
        const unique = opts
          .filter((o) => (seen.has(o.label) ? false : seen.add(o.label)))
          .sort((a, b) => a.label.localeCompare(b.label, 'fr'));
        if (unique.length) setVideoProcessorOptions(unique);
      } catch (error) {
        console.error('Error loading video processor catalog (fallback to static list):', error);
      }
    };
    fetchVideoProcessorCatalog();
  }, []);

  useEffect(() => {
    // Garde le focus sur le champ de scan : focus initial à l'étape 1 ET
    // re-focus automatique dès que le champ est réactivé après un scan
    // (l'input est désactivé pendant le traitement, ce qui faisait perdre le focus).
    if (currentStep === 1 && !scanning && scanInputRef.current) {
      const t = setTimeout(() => scanInputRef.current?.focus(), 50);
      return () => clearTimeout(t);
    }
  }, [currentStep, scanning]);

  const fetchOrderDetails = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/orders/${reservationId}/details`, { 
        withCredentials: true 
      });
      setReservation(response.data.reservation);
      setVideoProcessor(response.data.reservation?.selected_video_processor || '');
      setPackComponents(response.data.pack_components || []);
      
      // Initialize scanned items from saved preparation progress.
      // preparation_progress is keyed by the stock item's REAL product_id.
      // Pour un processeur de remplacement (modèle ≠ composant d'origine), ce
      // product_id ne correspond à aucun composant : on reconstruit alors le
      // placement comme au moment du scan (emplacement vidéo processeur, puis
      // 1er emplacement libre) afin que l'article ne "disparaisse" pas.
      const savedProgress = response.data.preparation_progress || {};
      const components = response.data.pack_components || [];
      const initialScanned = {};
      components.forEach(comp => { initialScanned[comp.product_id] = []; });

      const placedSerials = new Set();
      // 1) Correspondance directe : progress dont la clé = product_id d'un composant
      components.forEach(comp => {
        const items = savedProgress[comp.product_id];
        if (Array.isArray(items) && items.length) {
          initialScanned[comp.product_id] = items.map(it => ({ ...it }));
          items.forEach(it => placedSerials.add(it.serial));
        }
      });

      // 2) Entrées restantes (product_id sans composant correspondant, ex.
      //    dalle 50×50 -> emplacement dalle 50×100, ou processeur de remplacement).
      //    On place dans le composant du même groupe interchangeable (non plein
      //    en équivalents), sinon emplacement vidéo processeur, sinon 1er libre.
      const slotFactor = (pid) => (initialScanned[pid] || []).reduce((s, it) => s + (it.equivalence_factor ?? 1), 0);
      const notFull = (c) => slotFactor(c.product_id) < (c.quantity - 1e-9);
      Object.entries(savedProgress).forEach(([pid, items]) => {
        if (components.some(c => c.product_id === pid)) return; // déjà traité
        (items || []).forEach(it => {
          if (placedSerials.has(it.serial)) return;
          let target = (it.alternative_group && components.find(
            c => c.alternative_group === it.alternative_group && notFull(c)
          )) || components.find(
            c => c.is_video_processor && notFull(c)
          ) || components.find(
            c => notFull(c)
          );
          if (target) {
            initialScanned[target.product_id] = [...(initialScanned[target.product_id] || []), { ...it }];
            placedSerials.add(it.serial);
          }
        });
      });
      setScannedItems(initialScanned);
      
    } catch (error) {
      console.error('Error fetching order details:', error);
      toast.error('Erreur lors du chargement de la commande');
      navigate('/admin');
    } finally {
      setLoading(false);
    }
  };

  const handleVideoProcessorChange = async (value) => {
    const previous = videoProcessor;
    setVideoProcessor(value);
    setSavingProcessor(true);
    // Modèle choisi -> récupère SKU + nom catalogue pour garder titre/SKU cohérents
    const opt = videoProcessorOptions.find((o) => o.label === value);
    // Met à jour le libellé ET le SKU de l'emplacement vidéo processeur en direct
    setPackComponents((prev) => prev.map((c) => (c.is_video_processor ? {
      ...c,
      original_product_name: c.original_product_name || c.product_name,
      original_product_sku: c.original_product_sku || c.product_sku,
      product_name: value ? (opt?.name || `Vidéo processeur ${value}`) : (c.original_product_name || c.product_name),
      product_sku: value ? (opt?.sku || c.product_sku) : (c.original_product_sku || c.product_sku),
      selected_video_processor: value,
    } : c)));
    try {
      await axios.put(
        `${API}/admin/orders/${reservationId}/video-processor`,
        { video_processor: value },
        { withCredentials: true }
      );
      toast.success(value ? `Vidéo processeur : ${value}` : "Modèle d'origine rétabli");
    } catch (error) {
      console.error('Error saving video processor:', error);
      setVideoProcessor(previous);
      toast.error("Erreur lors de l'enregistrement du vidéo processeur");
    } finally {
      setSavingProcessor(false);
    }
  };

  const handleScanKeyDown = async (e) => {
    if (e.key === 'Enter' && scanInput.trim()) {
      await processScannedSerial(scanInput.trim());
      setScanInput('');
    }
  };

  const itemFactor = (it) => (it?.equivalence_factor ?? 1);
  const slotFactorSum = (productId) => (scannedItems[productId] || []).reduce((s, it) => s + itemFactor(it), 0);
  // Besoin du composant exprimé en équivalents (base 50×100) : quantité × facteur.
  // Ex. composant 28×(50×50, f=0,5) => 14 équivalents ; 14×(50×100, f=1) => 14.
  const reqEquiv = (comp) => (comp.quantity || 0) * (comp.equivalence_factor ?? 1);
  const isSlotFull = (comp) => slotFactorSum(comp.product_id) >= reqEquiv(comp) - 1e-9;

  const processScannedSerial = async (serialNumber) => {
    const allScanned = Object.values(scannedItems).flat();
    if (allScanned.some(item => item.serial === serialNumber)) {
      toast.error(`N° ${serialNumber} déjà scanné !`);
      return;
    }

    setScanning(true);
    try {
      // Get item info (always returns 200 — stub returned if not in stock DB)
      const response = await axios.get(`${API}/admin/stock-items/by-serial/${serialNumber}`, {
        withCredentials: true
      });
      
      const stockItem = response.data;
      const isVirtual = stockItem.is_virtual || stockItem.status === 'VIRTUAL';
      
      // Block only if reserved for another order (real stock items only)
      if (!isVirtual && stockItem.status === 'RESERVE' && stockItem.current_reservation_id && stockItem.current_reservation_id !== reservationId) {
        toast.error(`Article ${serialNumber} déjà réservé pour une autre commande`);
        setScanning(false);
        return;
      }

      // Find matching component (direct match first, then via alternative_group)
      let matchingComponent = packComponents.find(comp => comp.product_id === stockItem.product_id);
      let targetProductId = stockItem.product_id;
      
      if (!matchingComponent && stockItem.alternative_group) {
        matchingComponent = packComponents.find(
          comp => comp.alternative_group === stockItem.alternative_group
        );
        if (matchingComponent) {
          targetProductId = matchingComponent.product_id;
        }
      }

      // Vidéo processeur : n'importe quel modèle remplit l'emplacement "vidéo processeur".
      // Permet de biper un modèle de remplacement en cas de rupture de stock.
      const isVideoProcessor = stockItem.category === 'VIDEO_PROCESSEUR'
        || (stockItem.product_name || '').toLowerCase().includes('processeur');
      if ((!matchingComponent || matchingComponent.product_id !== targetProductId) && isVideoProcessor) {
        const vpComponent = packComponents.find(
          comp => comp.is_video_processor && !isSlotFull(comp)
        );
        if (vpComponent) {
          matchingComponent = vpComponent;
          targetProductId = vpComponent.product_id;
        }
      }

      // Fallback for virtual / unknown items: auto-assign to next unfilled component
      if (!matchingComponent) {
        matchingComponent = packComponents.find(
          comp => !isSlotFull(comp)
        );
        if (matchingComponent) {
          targetProductId = matchingComponent.product_id;
        }
      }
      
      if (!matchingComponent) {
        toast.error(`Tous les emplacements du pack sont déjà remplis`);
        setScanning(false);
        return;
      }

      // Capacité atteinte (en équivalents) : on tente une alternative du même
      // groupe / un autre emplacement libre, sinon on bloque.
      if (isSlotFull(matchingComponent)) {
        const altComponent = packComponents.find(
          comp => comp.product_id !== targetProductId
            && !isSlotFull(comp)
            && (
              !matchingComponent.alternative_group
              || comp.alternative_group === matchingComponent.alternative_group
              || isVirtual
            )
        );
        if (altComponent) {
          matchingComponent = altComponent;
          targetProductId = altComponent.product_id;
        } else {
          toast.error(`Tous les emplacements sont déjà remplis`);
          setScanning(false);
          return;
        }
      }

      // Assign item to this order
      await axios.post(`${API}/admin/orders/${reservationId}/assign-item`, 
        { serial_number: serialNumber },
        { withCredentials: true }
      );

      setScannedItems(prev => ({
        ...prev,
        [targetProductId]: [
          ...(prev[targetProductId] || []),
          {
            serial: serialNumber,
            stock_item_id: stockItem.id || null,
            condition: stockItem.condition_current || 'excellent',
            is_new: stockItem.condition_current === 'NEUF',
            product_name: stockItem.product_name || matchingComponent.product_name,
            is_virtual: isVirtual,
            equivalence_factor: stockItem.equivalence_factor ?? 1,
          }
        ]
      }));

      const label = isVirtual ? `Article ${serialNumber} ajouté (hors stock)` : `${stockItem.product_name} assigné !`;
      toast.success(label);
      
    } catch (error) {
      console.error('Error processing serial:', error);
      const errorMsg = error.response?.data?.detail || `N° série ${serialNumber} non trouvé`;
      toast.error(errorMsg);
    } finally {
      setScanning(false);
      scanInputRef.current?.focus();
    }
  };

  const removeScannedItem = async (productId, serial) => {
    try {
      // Unassign item from order
      await axios.post(`${API}/admin/orders/${reservationId}/unassign-item`,
        { serial_number: serial },
        { withCredentials: true }
      );
      
      setScannedItems(prev => ({
        ...prev,
        [productId]: prev[productId].filter(item => item.serial !== serial)
      }));
      toast.info('Article retiré et libéré');
    } catch (error) {
      console.error('Error unassigning item:', error);
      // Still remove from UI even if backend fails
      setScannedItems(prev => ({
        ...prev,
        [productId]: prev[productId].filter(item => item.serial !== serial)
      }));
      toast.warning('Article retiré (erreur serveur)');
    }
  };

  const getComponentProgress = (component) => {
    const items = scannedItems[component.product_id] || [];
    const physicalCount = items.length;
    const factorSum = items.reduce((s, it) => s + itemFactor(it), 0);
    const required = reqEquiv(component); // en équivalents (base 50×100)

    // Cible d'affichage dynamique selon la taille scannée (facteur uniforme).
    const factorsSet = new Set(items.map(it => itemFactor(it)));
    let unitFactor;
    if (items.length === 0) unitFactor = component.equivalence_factor ?? 1;
    else if (factorsSet.size === 1) unitFactor = [...factorsSet][0];
    else unitFactor = null; // tailles mixtes
    const displayTotal = unitFactor ? Math.round(required / unitFactor) : component.quantity;
    const displayScanned = unitFactor ? physicalCount : Math.round((factorSum / (component.equivalence_factor ?? 1)) * 100) / 100;

    const complete = factorSum >= required - 1e-9;
    const percent = required > 0 ? Math.min(100, Math.round((factorSum / required) * 100)) : 0;

    // Auto-complétion via un groupe alternatif (l'autre format de dalle/caisse)
    if (component.alternative_group && !complete) {
      const altGroupMembers = packComponents.filter(
        c => c.alternative_group === component.alternative_group && c.product_id !== component.product_id
      );
      const anyAltComplete = altGroupMembers.some(alt => slotFactorSum(alt.product_id) >= reqEquiv(alt) - 1e-9);
      if (anyAltComplete) {
        return { scanned: displayScanned, total: displayTotal, percent: 100, complete: true, autoCompleted: true };
      }
    }

    return { scanned: displayScanned, total: displayTotal, percent, complete, autoCompleted: false };
  };

  const getTotalProgress = () => {
    let totalItems = 0;
    let scannedCount = 0;
    const processedGroups = new Set();

    packComponents.forEach(comp => {
      if (comp.alternative_group) {
        if (processedGroups.has(comp.alternative_group)) return;
        processedGroups.add(comp.alternative_group);
        const groupMembers = packComponents.filter(c => c.alternative_group === comp.alternative_group);
        const target = Math.max(...groupMembers.map(m => reqEquiv(m)));
        const anyComplete = groupMembers.some(m => slotFactorSum(m.product_id) >= reqEquiv(m) - 1e-9);
        const bestProgress = Math.max(...groupMembers.map(m => slotFactorSum(m.product_id)));
        totalItems += target;
        scannedCount += anyComplete ? target : Math.min(target, bestProgress);
      } else {
        totalItems += reqEquiv(comp);
        scannedCount += Math.min(reqEquiv(comp), slotFactorSum(comp.product_id));
      }
    });

    const percent = totalItems > 0 ? Math.round((scannedCount / totalItems) * 100) : 0;
    return { scanned: Math.round(scannedCount), total: Math.round(totalItems), percent };
  };

  const isAllScanned = () => {
    return packComponents.every(comp => getComponentProgress(comp).complete);
  };

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    e.target.value = '';
    for (const file of files) {
      try {
        const data = await compressImage(file);
        setInspectionPhotos(prev => [...prev, { name: file.name, data, size: Math.round(data.length * 0.75) }]);
      } catch {
        toast.error(`Impossible de charger ${file.name}`);
      }
    }
  };

  const removePhoto = (index) => {
    setInspectionPhotos(prev => prev.filter((_, i) => i !== index));
  };

  const generateTrackingUrl = (trackingNumber) => {
    if (trackingNumber && shippingInfo.carrier === 'Dachser') {
      return `https://www.dachser.com/fr/fr/Suivi-des-envois_171.htm?trackingNumber=${trackingNumber}`;
    }
    return '';
  };

  const NO_TRACKING_CARRIERS = ['Livraison par Phocéa', 'Retrait sur place'];
  const noTrackingCarrier = NO_TRACKING_CARRIERS.includes(shippingInfo.carrier);

  const handleTrackingNumberChange = (value) => {
    setShippingInfo(prev => ({
      ...prev,
      trackingNumber: value,
      trackingUrl: generateTrackingUrl(value)
    }));
  };

  const handleSubmit = async () => {
    // Le n° de suivi n'est pas requis pour une livraison Phocéa / un retrait sur place
    if (!NO_TRACKING_CARRIERS.includes(shippingInfo.carrier) && !shippingInfo.trackingNumber.trim()) {
      toast.error('Veuillez saisir le N° de suivi');
      return;
    }

    setSubmitting(true);
    try {
      const assignedItems = [];
      Object.entries(scannedItems).forEach(([productId, items]) => {
        items.forEach(item => {
          assignedItems.push({
            stock_item_id: item.stock_item_id,
            serial_number: item.serial,
            product_id: productId,
            product_name: item.product_name,
            condition: item.condition,
            is_new: item.is_new,
            notes: ''
          });
        });
      });

      const payload = {
        assigned_items: assignedItems,
        departure_inspection: {
          photos: inspectionPhotos,
          notes: inspectionNotes,
          items_condition: assignedItems.map(item => ({
            serial_number: item.serial_number,
            product_name: item.product_name,
            condition: item.condition,
            is_new: item.is_new
          }))
        },
        shipping: {
          carrier: shippingInfo.carrier,
          tracking_number: shippingInfo.trackingNumber,
          tracking_url: shippingInfo.trackingUrl || generateTrackingUrl(shippingInfo.trackingNumber),
          estimated_delivery: shippingInfo.estimatedDelivery
        }
      };

      await axios.post(`${API}/admin/orders/${reservationId}/process`, payload, {
        withCredentials: true
      });

      toast.success('Commande expédiée avec succès !');
      navigate('/admin');
    } catch (error) {
      console.error('Error processing order:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors du traitement');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: '2-digit', month: '2-digit', year: 'numeric'
    });
  };

  const formatPrice = (amount) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amount || 0);
  };

  const getPackImage = () => {
    return getPackImageUrl(reservation?.pack_id, reservation?.pack_name);
  };

  // Download contract
  const handleDownloadContract = async () => {
    try {
      const response = await axios.get(`${API}/admin/orders/${reservationId}/contract`, {
        withCredentials: true
      });
      if (response.data.message) {
        toast.info(response.data.message);
      }
    } catch (error) {
      toast.error('Erreur lors du téléchargement du contrat');
    }
  };

  // Download RC document
  const handleDownloadRC = async () => {
    try {
      const response = await axios.get(`${API}/admin/orders/${reservationId}/rc-document`, {
        withCredentials: true
      });
      if (response.data.rc_document_url) {
        window.open(response.data.rc_document_url, '_blank');
      }
    } catch (error) {
      toast.error('Document RC non disponible');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="flex items-center gap-4">
          <Loader2 className="w-8 h-8 animate-spin text-[#DFFF00]" />
          <span className="text-white text-lg">Chargement de la commande...</span>
        </div>
      </div>
    );
  }

  const totalProgress = getTotalProgress();

  return (
    <div className="min-h-screen bg-black">
      {/* Header */}
      <header className="bg-slate-900 border-b border-slate-800 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => navigate('/admin')}
                className="text-slate-400 hover:text-white"
              >
                <ArrowLeft className="w-5 h-5 mr-2" />
                Retour
              </Button>
              <div className="h-6 w-px bg-slate-700" />
              <span className="text-slate-400 text-sm">Commande #{reservationId?.slice(0, 8)}</span>
            </div>
            
            {/* Steps indicator */}
            <div className="flex items-center gap-2">
              {[
                { num: 1, label: 'Préparation', icon: Package },
                { num: 2, label: 'État des lieux', icon: Camera },
                { num: 3, label: 'Expédition', icon: Truck }
              ].map((step, index) => (
                <React.Fragment key={step.num}>
                  <button
                    onClick={() => step.num <= currentStep && setCurrentStep(step.num)}
                    disabled={step.num > currentStep && !isAllScanned()}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                      currentStep === step.num 
                        ? 'bg-[#DFFF00] text-black' 
                        : step.num < currentStep
                          ? 'bg-green-500/20 text-green-400'
                          : 'bg-slate-800 text-slate-500'
                    }`}
                  >
                    {step.num < currentStep ? (
                      <Check className="w-4 h-4" />
                    ) : (
                      <step.icon className="w-4 h-4" />
                    )}
                    <span className="text-sm font-medium">{step.label}</span>
                  </button>
                  {index < 2 && (
                    <ChevronRight className={`w-4 h-4 ${currentStep > step.num ? 'text-green-400' : 'text-slate-600'}`} />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      </header>

      {/* Bandeau date de location souhaitée — info logistique critique */}
      {reservation?.rental_start_date && (
        <>
          <style>{`
            @keyframes phoceaFlash {
              0%, 100% { box-shadow: 0 0 0 0 rgba(223,255,0,0.65); }
              50% { box-shadow: 0 0 24px 6px rgba(223,255,0,0.55); }
            }
          `}</style>
          <div
            className="bg-[#DFFF00] text-black"
            style={{ animation: 'phoceaFlash 1.4s ease-in-out infinite' }}
            data-testid="rental-date-banner"
          >
            <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-center gap-3">
              <Calendar className="w-5 h-5 shrink-0" />
              <span className="font-bold text-sm md:text-base uppercase tracking-wide text-center">
                Date de location souhaitée : {formatDate(reservation.rental_start_date)}
              </span>
            </div>
          </div>
        </>
      )}

      {/* Main content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Pack Header with Image */}
        <div className="mb-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Pack Image and Info */}
          <div className="lg:col-span-1">
            <div className="bg-slate-900 rounded-2xl border border-slate-800 overflow-hidden">
              <div className="aspect-[4/3] relative">
                <img 
                  src={getPackImage()} 
                  alt={reservation?.pack_name}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <p className="text-[#DFFF00] text-sm font-medium mb-1">Commande #{reservationId?.slice(0, 8)}</p>
                  <h1 className="text-3xl font-bold text-white">{reservation?.pack_name}</h1>
                  <p className="text-slate-300">{reservation?.pack_surface} • {formatPrice(reservation?.monthly_price)}/mois</p>
                </div>
              </div>
              
              {/* Customer Info */}
              <div className="p-4 border-t border-slate-800">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-[#DFFF00]/20 flex items-center justify-center">
                    <User className="w-5 h-5 text-[#DFFF00]" />
                  </div>
                  <div>
                    <p className="text-white font-medium">{reservation?.customer?.firstName} {reservation?.customer?.lastName}</p>
                    {reservation?.customer?.company && (
                      <p className="text-slate-400 text-sm">{reservation?.customer?.company}</p>
                    )}
                  </div>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2 text-slate-400">
                    <Mail className="w-4 h-4" />
                    <span>{reservation?.customer?.email}</span>
                  </div>
                  <div className="flex items-center gap-2 text-slate-400">
                    <Phone className="w-4 h-4" />
                    <span>{reservation?.customer?.phone}</span>
                  </div>
                  <div className="flex items-start gap-2 text-slate-400">
                    <MapPin className="w-4 h-4 mt-0.5" />
                    <span data-testid="customer-delivery-address">
                      {getDeliveryAddress(reservation).full || 'Adresse non renseignée'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Contract, RC & Payment Info */}
          <div className="lg:col-span-2 space-y-4">
            {/* Documents Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Contract */}
              <div className="bg-slate-900 rounded-xl border border-slate-800 p-4">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                    <FileText className="w-5 h-5 text-blue-400" />
                  </div>
                  <div>
                    <h3 className="text-white font-medium">Contrat (CGV)</h3>
                    <p className="text-xs text-slate-400">Conditions générales signées</p>
                  </div>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-slate-400">Statut</span>
                    <span className="flex items-center gap-1 text-green-400">
                      <CheckCircle className="w-4 h-4" />
                      Signé
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-400">Signé le</span>
                    <span className="text-white">{formatDate(reservation?.contract_accepted_at)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-400">IP</span>
                    <span className="text-slate-300 font-mono text-xs">{reservation?.contract_signed_ip || '-'}</span>
                  </div>
                </div>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="w-full mt-3 border-slate-700 text-slate-300"
                  onClick={handleDownloadContract}
                >
                  <Download className="w-4 h-4 mr-2" />
                  Télécharger le contrat
                </Button>
              </div>

              {/* RC Pro */}
              <div className="bg-slate-900 rounded-xl border border-slate-800 p-4">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                    <Shield className="w-5 h-5 text-purple-400" />
                  </div>
                  <div>
                    <h3 className="text-white font-medium">RC Professionnelle</h3>
                    <p className="text-xs text-slate-400">Assurance responsabilité civile</p>
                  </div>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-slate-400">Statut</span>
                    {reservation?.rc_document_name ? (
                      <span className="flex items-center gap-1 text-green-400">
                        <CheckCircle className="w-4 h-4" />
                        Uploadé
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-orange-400">
                        <AlertTriangle className="w-4 h-4" />
                        Non fourni
                      </span>
                    )}
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-400">Document</span>
                    <span className="text-white text-xs truncate max-w-[150px]">
                      {reservation?.rc_document_name || '-'}
                    </span>
                  </div>
                </div>
                {reservation?.rc_document_name && (
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="w-full mt-3 border-slate-700 text-slate-300"
                    onClick={handleDownloadRC}
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Télécharger la RC
                  </Button>
                )}
              </div>
            </div>

            {/* Stripe Payment Info */}
            <div className="bg-slate-900 rounded-xl border border-slate-800 p-4">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                  <CreditCard className="w-5 h-5 text-green-400" />
                </div>
                <div>
                  <h3 className="text-white font-medium">Paiement Stripe</h3>
                  <p className="text-xs text-slate-400">Détails de la transaction</p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-xs text-slate-500 mb-1">Abonnement mensuel</p>
                  <p className="text-white font-semibold">{formatPrice(reservation?.monthly_price)}/mois</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 mb-1">Caution</p>
                  <p className="text-white font-semibold">{formatPrice(reservation?.caution)}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 mb-1">Session ID</p>
                  <p className="text-[#DFFF00] font-mono text-xs truncate">
                    {reservation?.stripe_session_id?.slice(0, 24)}...
                  </p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 mb-1">Customer ID</p>
                  <p className="text-slate-300 font-mono text-xs truncate">
                    {reservation?.stripe_customer_id?.slice(0, 20)}...
                  </p>
                </div>
              </div>
              
              {reservation?.stripe_subscription_id && (
                <div className="mt-3 pt-3 border-t border-slate-800">
                  <p className="text-xs text-slate-500 mb-1">Subscription ID</p>
                  <p className="text-slate-300 font-mono text-xs">
                    {reservation?.stripe_subscription_id}
                  </p>

                  {/* Anniversaire de prélèvement */}
                  <div className="mt-4 pt-4 border-t border-slate-800">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="text-xs text-slate-500 mb-1">Date d'anniversaire de prélèvement</p>
                        <p className="text-sm text-white">
                          {reservation?.billing_anniversary_day
                            ? <>Le <span className="font-bold text-[#DFFF00]">{reservation.billing_anniversary_day}</span> de chaque mois {reservation?.billing_anniversary_shifted_to && <span className="text-slate-400">— prochain prélèvement le {formatDate(reservation.billing_anniversary_shifted_to)}</span>}</>
                            : <span className="text-slate-400">Jour d'origine du paiement (calculé par Stripe)</span>}
                        </p>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-slate-700 hover:bg-slate-800 text-slate-200 shrink-0"
                        data-testid="shift-anniversary-btn"
                        onClick={async () => {
                          const dayStr = window.prompt(
                            "Sur quel jour du mois décaler le prélèvement ? (1 à 28)",
                            String(reservation?.billing_anniversary_day || 21)
                          );
                          if (!dayStr) return;
                          const day = parseInt(dayStr, 10);
                          if (isNaN(day) || day < 1 || day > 28) {
                            toast.error("Jour invalide (1 à 28)");
                            return;
                          }
                          const reason = window.prompt("Motif (optionnel) ?", "Décalage à la demande du client") || "";
                          if (!window.confirm(
                            `Décaler le cycle au ${day} de chaque mois ?\n\n`
                            + `• Date de début de location : prochain ${day} ≥ aujourd'hui\n`
                            + `• 1er prélèvement décalé : ${day} du mois suivant (pas de prorata)\n`
                            + `• Mensualités suivantes : tous les ${day} jusqu'à résiliation`
                          )) return;
                          try {
                            const resp = await axios.post(`${API}/admin/orders/${reservation.id}/shift-anniversary`, {
                              day_of_month: day,
                              skip_proration: true,
                              reason,
                            }, { withCredentials: true });
                            toast.success(
                              `Début location : ${resp.data.rental_start_date_human} · Prochain prélèvement : ${resp.data.next_billing_date_human}`
                            );
                            fetchOrderDetails();
                          } catch (e) {
                            toast.error(e.response?.data?.detail || "Erreur lors du décalage");
                          }
                        }}
                      >
                        <Calendar className="w-4 h-4 mr-2" />
                        Décaler l'anniversaire
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Step 1: Scanning */}
        {currentStep === 1 && (
          <div className="space-y-6">
            {/* Global Progress */}
            <div className="bg-slate-900 rounded-xl border border-slate-800 p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <Scan className="w-5 h-5 text-[#DFFF00]" />
                  <span className="text-white font-medium">Progression globale</span>
                </div>
                <span className={`font-bold ${totalProgress.percent === 100 ? 'text-green-400' : 'text-[#DFFF00]'}`}>
                  {totalProgress.scanned} / {totalProgress.total} articles
                </span>
              </div>
              <div className="h-3 bg-slate-800 rounded-full overflow-hidden">
                <div 
                  className={`h-full transition-all duration-500 ${totalProgress.percent === 100 ? 'bg-green-500' : 'bg-[#DFFF00]'}`}
                  style={{ width: `${totalProgress.percent}%` }}
                />
              </div>
            </div>

            {/* Scan input */}
            <div className="bg-slate-900 rounded-xl border border-slate-800 p-6">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 rounded-xl bg-[#DFFF00]/20 flex items-center justify-center">
                  <Scan className="w-6 h-6 text-[#DFFF00]" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">Scanner les articles</h2>
                  <p className="text-slate-400">Scannez ou saisissez les numéros de série du matériel</p>
                </div>
              </div>
              
              <div className="flex gap-4">
                <div className="flex-1 relative">
                  <Input
                    ref={scanInputRef}
                    placeholder="Scannez un code-barres ou saisissez le N° de série..."
                    className="bg-slate-800 border-slate-700 text-white text-lg py-6 pl-12 font-mono"
                    value={scanInput}
                    onChange={(e) => setScanInput(e.target.value)}
                    onKeyDown={handleScanKeyDown}
                    disabled={scanning}
                  />
                  <Scan className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                  {scanning && (
                    <Loader2 className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#DFFF00] animate-spin" />
                  )}
                </div>
                <Button
                  className="bg-[#DFFF00] hover:bg-[#c8e600] text-black px-8"
                  onClick={() => scanInput.trim() && processScannedSerial(scanInput.trim())}
                  disabled={scanning || !scanInput.trim()}
                >
                  <Plus className="w-5 h-5 mr-2" />
                  Ajouter
                </Button>
              </div>
            </div>

            {/* Products list with progress bars */}
            <div className="bg-slate-900 rounded-xl border border-slate-800 overflow-hidden">
              <div className="p-4 border-b border-slate-800">
                <h3 className="text-lg font-semibold text-white">Contenu du pack</h3>
                <p className="text-sm text-slate-400">Liste des articles à scanner</p>
              </div>
              
              <div className="divide-y divide-slate-800">
                {packComponents.map((component, compIndex) => {
                  const progress = getComponentProgress(component);
                  const isComplete = progress.complete;
                  const isAutoCompleted = progress.autoCompleted;
                  
                  // Show "OU" separator between alternative group members
                  const showAltSeparator = component.alternative_group && compIndex > 0 &&
                    packComponents[compIndex - 1]?.alternative_group === component.alternative_group;
                  
                  return (
                    <React.Fragment key={component.product_id}>
                      {showAltSeparator && (
                        <div className="flex items-center justify-center py-2 bg-slate-800/50">
                          <span className="px-4 py-1 bg-amber-500/20 text-amber-400 text-xs font-bold rounded-full uppercase tracking-wider">
                            ou (alternatif)
                          </span>
                        </div>
                      )}
                      <div 
                        className={`p-4 transition-all ${isComplete ? 'bg-green-500/5' : ''} ${isAutoCompleted ? 'opacity-60' : ''}`}
                      >
                        {/* Product header with progress */}
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-3">
                            {isComplete ? (
                              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isAutoCompleted ? 'bg-green-500/50' : 'bg-green-500'}`}>
                                <Check className="w-6 h-6 text-white" />
                              </div>
                            ) : (
                              <div className="w-10 h-10 rounded-full bg-slate-700 flex items-center justify-center">
                                <Package className="w-5 h-5 text-slate-400" />
                              </div>
                            )}
                            <div>
                              <h4 className={`font-medium ${isComplete ? 'text-green-400' : 'text-white'}`}>
                                {component.product_name}
                                {isAutoCompleted && (
                                  <span className="ml-2 text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded-full">
                                    Alternatif OK
                                  </span>
                                )}
                              </h4>
                              <p className="text-xs text-slate-500">
                                {component.product_sku}
                                {component.alternative_group && (
                                  <span className="ml-2 text-amber-400/60">interchangeable</span>
                                )}
                              </p>
                            </div>
                          </div>
                          
                          <div className="text-right">
                            <p className={`text-2xl font-bold ${isComplete ? 'text-green-400' : 'text-white'}`}>
                              {isAutoCompleted ? <Check className="w-6 h-6 inline" /> : `${progress.scanned}/${progress.total}`}
                            </p>
                            {!isAutoCompleted && (
                              <p className="text-xs text-slate-500">{progress.percent}%</p>
                            )}
                          </div>
                        </div>
                        
                        {/* Progress bar */}
                        {!isAutoCompleted && (
                          <div className="h-2 bg-slate-800 rounded-full overflow-hidden mb-3">
                            <div 
                              className={`h-full transition-all duration-500 ${isComplete ? 'bg-green-500' : 'bg-[#DFFF00]'}`}
                              style={{ width: `${progress.percent}%` }}
                            />
                          </div>
                        )}
                        
                        {/* Scanned items */}
                        {progress.scanned > 0 && !isAutoCompleted && (
                          <div className="flex flex-wrap gap-2">
                            {scannedItems[component.product_id]?.map((item) => (
                              <div 
                                key={item.serial}
                                className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm ${
                                  isComplete ? 'bg-green-500/20 text-green-400' : 'bg-slate-800 text-white'
                                }`}
                              >
                                <Check className="w-4 h-4" />
                                <span className="font-mono">{item.serial}</span>
                                <button 
                                  className="ml-1 text-red-400 hover:text-red-300"
                                  onClick={() => removeScannedItem(component.product_id, item.serial)}
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Sélecteur de modèle — emplacement vidéo processeur (modulable) */}
                        {component.is_video_processor && (
                          <div className="mt-3 pt-3 border-t border-slate-800">
                            <label className="block text-xs font-semibold text-[#DFFF00] mb-1.5 uppercase tracking-wide">
                              Modèle à préparer (modifiable si rupture)
                            </label>
                            <div className="relative">
                              <select
                                data-testid="video-processor-select"
                                value={videoProcessor}
                                onChange={(e) => handleVideoProcessorChange(e.target.value)}
                                disabled={savingProcessor}
                                className="w-full bg-slate-800 border border-slate-700 text-white rounded-lg py-2.5 px-3 pr-9 text-sm appearance-none focus:outline-none focus:border-[#DFFF00] disabled:opacity-60 cursor-pointer"
                              >
                                <option value="">— Modèle d'origine ({component.original_product_name || component.product_name}) —</option>
                                {videoProcessorOptions.map((opt) => (
                                  <option key={opt.label} value={opt.label}>{opt.label}</option>
                                ))}
                              </select>
                              {savingProcessor ? (
                                <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#DFFF00] animate-spin pointer-events-none" />
                              ) : (
                                <ChevronRight className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 rotate-90 pointer-events-none" />
                              )}
                            </div>
                            <p className="mt-1.5 text-xs text-slate-500">
                              Bipez le modèle sélectionné — n'importe quel vidéo processeur remplira cet emplacement.
                            </p>
                          </div>
                        )}
                      </div>
                    </React.Fragment>
                  );
                })}
              </div>
            </div>

            {/* Next button */}
            <div className="flex justify-end">
              <Button
                className={`px-8 py-6 text-lg ${
                  isAllScanned() 
                    ? 'bg-[#DFFF00] hover:bg-[#c8e600] text-black' 
                    : 'bg-slate-700 text-slate-400 cursor-not-allowed'
                }`}
                disabled={!isAllScanned()}
                onClick={() => setCurrentStep(2)}
              >
                Continuer vers l'état des lieux
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 2: Inspection */}
        {currentStep === 2 && (
          <div className="space-y-6">
            <div className="bg-slate-900 rounded-xl border border-slate-800 p-6">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 rounded-xl bg-[#DFFF00]/20 flex items-center justify-center">
                  <Camera className="w-6 h-6 text-[#DFFF00]" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">État des lieux de départ</h2>
                  <p className="text-slate-400">Prenez des photos du matériel avant expédition</p>
                </div>
              </div>

              {/* Photo upload */}
              <div className="mb-6">
                <input
                  type="file"
                  id="photo-upload"
                  className="hidden"
                  accept="image/*"
                  multiple
                  onChange={handlePhotoUpload}
                />
                <label
                  htmlFor="photo-upload"
                  className="flex items-center justify-center gap-3 p-8 border-2 border-dashed border-slate-700 rounded-xl cursor-pointer hover:border-[#DFFF00] hover:bg-slate-800/50 transition-all"
                >
                  <Upload className="w-8 h-8 text-slate-400" />
                  <span className="text-slate-400">Cliquez ou glissez des photos ici</span>
                </label>
              </div>

              {/* Photo preview */}
              {inspectionPhotos.length > 0 && (
                <div className="grid grid-cols-4 gap-4 mb-6">
                  {inspectionPhotos.map((photo, index) => (
                    <div key={index} className="relative group aspect-video rounded-lg overflow-hidden border border-slate-700">
                      <img 
                        src={photo.data} 
                        alt={photo.name}
                        className="w-full h-full object-cover"
                      />
                      <button
                        className="absolute top-2 right-2 w-8 h-8 bg-red-500 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={() => removePhoto(index)}
                      >
                        <X className="w-5 h-5 text-white" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Notes */}
              <div>
                <label className="text-sm text-slate-400 mb-2 block">Notes / Observations</label>
                <textarea
                  className="w-full bg-slate-800 border border-slate-700 rounded-xl p-4 text-white resize-none"
                  rows={4}
                  placeholder="Remarques sur l'état général du matériel..."
                  value={inspectionNotes}
                  onChange={(e) => setInspectionNotes(e.target.value)}
                />
              </div>
            </div>

            {/* Articles summary with condition */}
            <div className="bg-slate-900 rounded-xl border border-slate-800 p-6">
              <h3 className="text-lg font-semibold text-white mb-4">Récapitulatif des articles</h3>
              <div className="space-y-2">
                {Object.entries(scannedItems).flatMap(([productId, items]) =>
                  items.map(item => (
                    <div key={item.serial} className="flex items-center justify-between p-3 bg-slate-800 rounded-lg">
                      <div className="flex items-center gap-3">
                        <Check className="w-5 h-5 text-green-400" />
                        <span className="text-white">{item.product_name}</span>
                        <span className="font-mono text-slate-400 text-sm">{item.serial}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <select
                          value={item.condition}
                          onChange={(e) => {
                            setScannedItems(prev => ({
                              ...prev,
                              [productId]: prev[productId].map(i => 
                                i.serial === item.serial ? { ...i, condition: e.target.value } : i
                              )
                            }));
                          }}
                          className="bg-slate-700 border border-slate-600 rounded px-3 py-1 text-sm text-white"
                        >
                          <option value="excellent">Excellent</option>
                          <option value="bon">Bon</option>
                          <option value="usage">Usagé</option>
                          <option value="abime">Abîmé</option>
                        </select>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Navigation */}
            <div className="flex justify-between">
              <Button
                variant="outline"
                className="border-slate-700 text-slate-300"
                onClick={() => setCurrentStep(1)}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Retour
              </Button>
              <Button
                className="bg-[#DFFF00] hover:bg-[#c8e600] text-black px-8"
                onClick={() => setCurrentStep(3)}
              >
                Continuer vers l'expédition
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Shipping */}
        {currentStep === 3 && (
          <div className="space-y-6">
            <div className="bg-slate-900 rounded-xl border border-slate-800 p-6">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 rounded-xl bg-[#DFFF00]/20 flex items-center justify-center">
                  <Truck className="w-6 h-6 text-[#DFFF00]" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">Informations d'expédition</h2>
                  <p className="text-slate-400">Saisissez les informations de livraison</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="text-sm text-slate-400 mb-2 block">Transporteur</label>
                  <select
                    className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-3 text-white"
                    value={shippingInfo.carrier}
                    onChange={(e) => setShippingInfo(prev => ({ ...prev, carrier: e.target.value }))}
                    data-testid="process-carrier-select"
                  >
                    <option value="Dachser">Dachser</option>
                    <option value="Geodis">Geodis</option>
                    <option value="TNT">TNT</option>
                    <option value="DHL">DHL</option>
                    <option value="Chronopost">Chronopost</option>
                    <option value="GLS">GLS</option>
                    <option value="Livraison par Phocéa">Livraison par Phocéa</option>
                    <option value="Retrait sur place">Retrait sur place</option>
                  </select>
                  <p className="text-xs text-slate-500 mt-1.5" data-testid="delivery-billing-note">
                    {shippingInfo.carrier === 'Retrait sur place'
                      ? "Retrait sur place : le client récupère le matériel — l'abonnement démarre immédiatement (prochain prélèvement dans 1 mois)."
                      : shippingInfo.carrier === 'Livraison par Phocéa'
                      ? "Livraison interne : l'abonnement démarre immédiatement (prochain prélèvement dans 1 mois)."
                      : "Transporteur : livraison estimée à J+3 → l'abonnement démarre à ce moment (prochain prélèvement 1 mois après)."}
                  </p>
                </div>
                
                <div>
                  <label className="text-sm text-slate-400 mb-2 block">
                    N° de suivi {noTrackingCarrier ? '(optionnel)' : '*'}
                  </label>
                  <Input
                    className="bg-slate-800 border-slate-700 text-white font-mono py-3"
                    placeholder={noTrackingCarrier ? 'Non requis pour ce mode de livraison' : 'Ex: 12345678901234'}
                    value={shippingInfo.trackingNumber}
                    onChange={(e) => handleTrackingNumberChange(e.target.value)}
                    data-testid="process-tracking-input"
                  />
                </div>
                
                <div className="md:col-span-2">
                  <label className="text-sm text-slate-400 mb-2 block">Lien de suivi</label>
                  <div className="flex gap-3">
                    <Input
                      className="bg-slate-800 border-slate-700 text-white flex-1"
                      placeholder="URL de suivi (auto-généré pour Dachser)"
                      value={shippingInfo.trackingUrl}
                      onChange={(e) => setShippingInfo(prev => ({ ...prev, trackingUrl: e.target.value }))}
                    />
                    {shippingInfo.trackingUrl && (
                      <Button
                        variant="outline"
                        className="border-slate-700"
                        onClick={() => window.open(shippingInfo.trackingUrl, '_blank')}
                      >
                        <ExternalLink className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
                
                <div>
                  <label className="text-sm text-slate-400 mb-2 block">Date de livraison estimée</label>
                  <Input
                    type="date"
                    className="bg-slate-800 border-slate-700 text-white"
                    value={shippingInfo.estimatedDelivery}
                    onChange={(e) => setShippingInfo(prev => ({ ...prev, estimatedDelivery: e.target.value }))}
                  />
                </div>
              </div>
            </div>

            {/* Delivery address */}
            <div className="bg-slate-900 rounded-xl border border-slate-800 p-6">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <MapPin className="w-5 h-5 text-[#DFFF00]" />
                Adresse de livraison
              </h3>
              <div className="bg-slate-800 rounded-lg p-4">
                <p className="text-white font-medium">
                  {reservation?.customer?.firstName} {reservation?.customer?.lastName}
                </p>
                {reservation?.customer?.company && (
                  <p className="text-slate-400">{reservation?.customer?.company}</p>
                )}
                <p className="text-slate-400 mt-2">{getDeliveryAddress(reservation).street || 'Adresse non renseignée'}</p>
                <p className="text-slate-400">{getDeliveryAddress(reservation).line2}</p>
              </div>
            </div>

            {/* Summary */}
            <div className="bg-gradient-to-br from-[#DFFF00]/10 to-transparent rounded-xl border border-[#DFFF00]/30 p-6">
              <h3 className="text-lg font-semibold text-white mb-4">Récapitulatif</h3>
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <p className="text-3xl font-bold text-[#DFFF00]">
                    {Object.values(scannedItems).flat().length}
                  </p>
                  <p className="text-slate-400">Articles scannés</p>
                </div>
                <div>
                  <p className="text-3xl font-bold text-[#DFFF00]">{inspectionPhotos.length}</p>
                  <p className="text-slate-400">Photos</p>
                </div>
                <div>
                  <p className="text-3xl font-bold text-[#DFFF00]">
                    {shippingInfo.trackingNumber ? '1' : '0'}
                  </p>
                  <p className="text-slate-400">N° suivi</p>
                </div>
              </div>
            </div>

            {/* Navigation */}
            <div className="flex justify-between">
              <Button
                variant="outline"
                className="border-slate-700 text-slate-300"
                onClick={() => setCurrentStep(2)}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Retour
              </Button>
              <Button
                className="bg-[#DFFF00] hover:bg-[#c8e600] text-black px-8 py-6 text-lg"
                disabled={(!noTrackingCarrier && !shippingInfo.trackingNumber.trim()) || submitting}
                onClick={handleSubmit}
                data-testid="process-submit-btn"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Traitement...
                  </>
                ) : (
                  <>
                    <Truck className="w-5 h-5 mr-2" />
                    Valider l'expédition
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default OrderProcessingPage;
